import { action, computed, makeObservable, observable } from "mobx";
import { toastError, toastSuccess } from "../utils/notification";
import { createBook, updateBook } from "../requests/book-creator";
import { BookDetailModel, BookModel } from "@/types/book";

export interface Page {
  index: number;
  image: string;
  remark?: string;
}

interface LocalBookModel {
  name: string;
  cover: string;
  width: number;
  height: number;
}

interface FetchCreateBookArguments {
  title: string;
  pages: Array<string>;
  cover: string;
  width: number;
  height: number;
}
interface FetchUpdateBookArguments extends FetchCreateBookArguments {
  id: number;
}

class BookCreatorProvider {
  private _pages: Map<number, Page> = new Map();
  count: number = 0;
  public book: Partial<LocalBookModel> = {};
  private bookModel: Nullable<BookModel>;
  public bookDetailModel: Nullable<BookDetailModel>;
  public initialized: boolean = false;
  /**
   * 在未进入创建页面时将选择好的图片保存下来，在进入页面后第一时间将保存的图片应用到创建页面，并清空该数组
   */
  private page_images_temp = new Array<string>();
  /**
   * 当前翻页页面序号
   */
  public currentPage: number = 0;
  public get pages() {
    return Array.from(this._pages.values());
  }
  public firstPageData(): Promise<
    Nullable<{
      width: number;
      height: number;
      src: string;
    }>
  > {
    return new Promise((r, v) => {
      const firstPage = this._pages.get(0);
      if (!firstPage) r(null);
      if (firstPage?.image === undefined) r(null);
      const image = new Image();
      image.onload = () => {
        r({
          width: image.width,
          height: image.height,
          src: firstPage?.image ?? "",
        });
      };
      image.onerror = () => {
        // toastError(
        //   `首页图片加载失败${`${process.env.REACT_APP_IMAGE_BASE_URL}${firstPage?.image}`}`
        // );
        console.log(
          `首页图片加载失败${`${process.env.REACT_APP_IMAGE_BASE_URL}${firstPage?.image}`}`
        );
        r(null);
      };
      image.src = `${firstPage?.image}`;
    });
  }
  constructor() {
    makeObservable(this, {
      count: observable,
      book: observable,
      initialized: observable,
      currentPage: observable,
      pages: computed,
      handleAppendPage: action,
      handleAppendMulPage: action,
      setBookName: action,
      performSweep: action,
      handleDeletePage: action,
      performDeleteAll: action,
      sortByArray: action,
      debug: action,
      applyBook: action,
    });
  }
  handleAppendPage(pageImage: string) {
    const index = this.count;
    const page = {
      index: index,
      image: pageImage,
    };
    this._pages.set(index, page);
    this.count = this._pages.size;
  }
  /**
   * 暂存外部图片，详见 @page_images_temp
   */
  handleAttachTempImages(pageImages: Array<string>) {
    console.log("存入自动", this.page_images_temp);
    if (!this.initialized) {
      this.page_images_temp = pageImages;
    } else {
      this.handleAppendMulPage(pageImages);
    }
  }

  /**
   * 使用外部图片，调用时机为 @setBookName
   */
  public handleDetachTempImages() {
    console.log("载入自动", this.page_images_temp);
    this.handleAppendMulPage(this.page_images_temp);
    this.page_images_temp = [];
  }
  handleAppendMulPage(pageImages: Array<string>) {
    for (let i = 0; i < pageImages.length; i++) {
      const index = this.count + i;
      const page = {
        index: index,
        image: pageImages[i],
      };
      this._pages.set(index, page);
    }
    this.count = this._pages.size;
    console.log("添加多页", this._pages);
  }
  setBookName(name: string) {
    this.book.name = name;
    this.initialized = true;
  }
  setBookSize(width: number, height: number) {
    this.book.width = width;
    this.book.height = height;
  }
  /**
   * 保存草稿
   */
  async handleSaveDraft(): Promise<boolean> {
    if (this.count < 2) {
      toastError("不能小于2页");
      return Promise.resolve(false);
    }
    console.log("存储的BookModel", this.bookModel);
    const id = this.bookModel?.id;
    console.log(
      "最新",
      this.pages.map((_) => _.image)
    );
    if (id)
      return this.fetchUpdateBook({
        id: id,
        title: this.book?.name ?? "",
        cover: this.pages[0].image,
        pages: this.pages.map((_) => _.image),
      });
    else {
      //自动根据第一页设置尺寸
      if (!this.book?.width || !this.book?.height) {
        //  toastError("不合法尺寸");
        //  return Promise.resolve(false);
        const firstPage = await this.firstPageData();
        console.log("首页数据", firstPage);
        if (!firstPage) {
          toastError("不合法尺寸");
          return Promise.resolve(false);
        } else {
          const { width, height } = firstPage;
          this.setBookSize(width, height);
        }
      }
      if (this.book?.width === 0 || this.book?.height === 0) {
        toastError("不合法尺寸");
        return Promise.resolve(false);
      }
      return this.fetchCreateBook({
        title: this.book?.name ?? "",
        cover: this.pages[0].image,
        pages: this.pages.map((_) => _.image),
        width: this.book?.width,
        height: this.book?.height,
      });
    }
    // return Promise.resolve(true);
  }
  public handleDeletePage(page: Page) {
    const page_arr = this.pages.filter((_) => _.index !== page.index);
    const newPages = new Map();
    for (let i = 0; i < page_arr.length; i++) {
      newPages.set(page_arr[i].index, page_arr[i]);
    }
    this._pages = newPages;
    this.count = this._pages.size;
  }
  private async fetchUpdateBook(
    args: Partial<FetchUpdateBookArguments>
  ): Promise<boolean> {
    const res = await updateBook(args);
    if (res?.success) {
      toastSuccess(res?.message);
      return Promise.resolve(true);
    } else {
      toastError(res?.message);
    }
    return Promise.resolve(false);
  }
  private async fetchCreateBook(
    args: Partial<FetchCreateBookArguments>
  ): Promise<boolean> {
    const res = await createBook(args);
    console.log("保存返回", res);
    if (res?.success) {
      toastSuccess(res?.message);
      this.bookModel = res?.data;
      return Promise.resolve(true);
    } else {
      toastError(res?.message);
    }
    return Promise.resolve(false);
  }
  public performDeleteAll() {
    this._pages.clear();
    this.count = 0;
  }
  /**
   * 通过传入排序数组重新排序map
   */
  public sortByArray(order: Array<number>) {
    const newPages = new Map();
    for (let i = 0; i < order.length; i++) {
      newPages.set(order[i], this._pages.get(order[i]));
    }
    this._pages = newPages;
  }
  public performSweep() {
    this.book = {};
    this.performDeleteAll();
    this.initialized = false;
    this.bookModel = null;
  }
  public debug() {
    this._pages = new Map([
      [
        0,
        {
          index: 0,
          image: "/media/uploads/测试PDF/1.png",
          remark: "0个",
        },
      ],
      [
        1,
        {
          index: 1,
          image: "/media/uploads/测试PDF/2.png",
          remark: "1个",
        },
      ],
      [
        2,
        {
          index: 2,
          image: "/media/uploads/测试PDF/3.png",
          remark: "2个",
        },
      ],
      [
        3,
        {
          index: 3,
          image: "/media/uploads/测试PDF/4.png",
          remark: "3个",
        },
      ],
      [
        4,
        {
          index: 4,
          image: "/media/uploads/测试PDF/5.png",
        },
      ],
      [
        5,
        {
          index: 5,
          image: "/media/uploads/测试PDF/6.png",
        },
      ],
      [
        6,
        {
          index: 6,
          image: "/media/uploads/测试PDF/7.png",
        },
      ],
      [
        7,
        {
          index: 7,
          image: "/media/uploads/测试PDF/8.png",
        },
      ],
      [
        8,
        {
          index: 8,
          image: "/media/uploads/测试PDF/9.png",
        },
      ],
      [
        9,
        {
          index: 9,
          image: "/media/uploads/测试PDF/10.png",
        },
      ],
      [
        10,
        {
          index: 10,
          image: "/media/uploads/测试PDF/11.png",
        },
      ],
      [
        11,
        {
          index: 11,
          image: "/media/uploads/测试PDF/12.png",
        },
      ],
      [
        12,
        {
          index: 12,
          image: "/media/uploads/测试PDF/13.png",
        },
      ],
      [
        13,
        {
          index: 13,
          image: "/media/uploads/测试PDF/14.png",
        },
      ],
      [
        14,
        {
          index: 14,
          image: "/media/uploads/测试PDF/15.png",
        },
      ],
      [
        15,
        {
          index: 15,
          image: "/media/uploads/测试PDF/16.png",
        },
      ],
      [
        16,
        {
          index: 16,
          image: "/media/uploads/测试PDF/17.png",
        },
      ],
      [
        17,
        {
          index: 17,
          image: "/media/uploads/测试PDF/18.png",
        },
      ],
      [
        18,
        {
          index: 18,
          image: "/media/uploads/测试PDF/19.png",
        },
      ],
    ]);
    this.count = this._pages.size;
    this.initialized = true;
  }
  public applyBook(book: BookModel, book_detail: BookDetailModel) {
    this.performSweep();
    this.book.name = book.title;
    this.book.cover = book.cover;
    this.handleAppendMulPage(book_detail.pages);
    this.bookModel = book;
    this.initialized = true;
  }
}

const bookCreatorProvider = new BookCreatorProvider();

export default bookCreatorProvider;
